import { DateTime, Duration } from 'luxon';
import { TaskHelpers } from 'ontraccr-common';
import moment from 'moment';

import Permissions from '../auth/Permissions';
import PDFTableExport from '../common/pdf/PDFExport/PDFTableExport';

import { roundTotalRuntime } from '../helpers/time';
import { isNullOrUndefined, mergeSets, toTitleCase } from '../helpers/helpers';
import { taskIsOutsideWorkingHours } from '../common/clock/clock.helpers';
import { timeKeyToTitle } from './state/Timecard.constants';

import { loadManualEntryData, stripSecondsAndMilliseconds } from '../clock/ManualEntry/manualEntryHelpers';
import { generateResponseMap } from '../forms/ResponderHelpers';
import { getBase64ImageFromURL } from '../projects/ProjectScheduleOfValues/PDF/sov.pdf.helpers';
import { formatProjectLabelFromCompanySettings } from '../projects/projectHelpers';
import { isGoodTask, deriveTasksWithBuckets } from '../helpers/overtime/otHelpers';

const EMPTY_TEXT = '--';

export const filterResolvedPendingEntries = (pendingEntries = [], realEntries = []) => {
  if (!pendingEntries.length) return pendingEntries;
  const realFormIds = new Set();
  realEntries.forEach((entry) => {
    if (entry?.formId) realFormIds.add(entry.formId);
  });
  if (!realFormIds.size) return pendingEntries;
  return pendingEntries.filter((entry) => !entry?.formId || !realFormIds.has(entry.formId));
};

export default {};

// CONSTANTS:
export const EXPORT_TYPE_PDF = 'PDF Export';
export const EXPORT_TYPE_EXCEL = 'Excel Export';

export const getViewColumnToExportMap = (t) => ({
  name: 'NAME',
  date: 'DATE',
  day: 'DATE',
  totalTime: 'DURATION',
  hasViolation: 'VIOLATION',
  hasRejection: 'REJECTION',
  StartTime: 'START TIME',
  EndTime: 'END TIME',
  RegularStartTime: 'REGULAR START TIME',
  RegularEndTime: 'REGULAR END TIME',
  OTStartTime: 'OT START TIME',
  OTEndTime: 'OT END TIME',
  BreakStartTime: 'BREAK START TIME',
  BreakEndTime: 'BREAK END TIME',
  DoubleOTStartTime: 'DT OT START TIME',
  DoubleOTEndTime: 'DT OT END TIME',
  Duration: 'DURATION',
  RegularDuration: 'REGULAR DURATION',
  BreakDuration: 'BREAK DURATION',
  OTDuration: 'OT DURATION',
  DTOTDuration: 'DT OT DURATION',
  PTODuration: 'PTO DURATION',
  Type: 'TYPE',
  Project: `${t('Project').toUpperCase()} NAME`,
  Phase: 'PHASE',
  CostCode: 'COST CODE',
  Union: 'UNION',
  LocalClass: 'LOCAL CLASS',
  WorkClass: 'WORK CLASS',
  Notes: 'NOTES',
  Status: 'STATUS',
  ApprovedBy: 'APPROVED BY',
  enteredVia: 'ENTERED VIA',
});

const getBasePDFHeader = (t) => [
  { key: 'DATE', text: 'Date', style: 'tableHeader' },
  { key: 'NAME', text: 'Name', style: 'tableHeader' },
  { key: 'REGULAR START TIME', text: 'Regular Start Time', style: 'tableHeader' },
  { key: 'REGULAR END TIME', text: 'Regular End Time', style: 'tableHeader' },
  { key: 'BREAK START TIME', text: 'Break Start Time', style: 'tableHeader' },
  { key: 'BREAK END TIME', text: 'Break End Time', style: 'tableHeader' },
  { key: 'OT START TIME', text: 'OT Start Time', style: 'tableHeader' },
  { key: 'OT END TIME', text: 'OT End Time', style: 'tableHeader' },
  { key: 'DT OT START TIME', text: 'DT OT Start Time', style: 'tableHeader' },
  { key: 'DT OT END TIME', text: 'DT OT End Time', style: 'tableHeader' },
  { key: 'DURATION', text: 'Duration', style: 'tableHeader' },
  { key: 'REGULAR DURATION', text: 'Regular Duration', style: 'tableHeader' },
  { key: 'BREAK DURATION', text: 'Break Duration', style: 'tableHeader' },
  { key: 'OT DURATION', text: 'OT Duration', style: 'tableHeader' },
  { key: 'DT OT DURATION', text: 'DT Overtime Duration', style: 'tableHeader' },
  { key: 'PTO DURATION', text: 'PTO Duration', style: 'tableHeader' },
  { key: 'TYPE', text: 'Type', style: 'tableHeader' },
  { key: `${t('Project').toUpperCase()} NAME`, text: `${t('Project')}`, style: 'tableHeader' },
  { key: 'PHASE', text: 'Phase', style: 'tableHeader' },
  { key: 'COST CODE', text: 'Cost Code', style: 'tableHeader' },
  { key: 'UNION', text: 'Union', style: 'tableHeader' },
  { key: 'LOCAL CLASS', text: 'Local Class', style: 'tableHeader' },
  { key: 'WORK CLASS', text: 'Work Class', style: 'tableHeader' },
  { key: 'NOTES', text: 'Notes', style: 'tableHeader' },
  { key: 'ENTERED VIA', text: 'Entered Via', style: 'tableHeader' },
  { key: 'APPROVED BY', text: 'Approved By', style: 'tableHeader' },
  { key: 'VIOLATION', text: 'Violation', style: 'tableHeader' },
];

const shouldShowSignature = ({ tasks, summaryExportFields, dataMap }) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const task of tasks) {
    const { data: customData = [], divisionId } = dataMap[task.id] ?? {};
    const signatureField = summaryExportFields[divisionId];
    if (signatureField) {
      const customFieldResponseMap = generateResponseMap(customData);
      const { values = [] } = customFieldResponseMap[signatureField] ?? {};
      if (values.length) return true;
    }
  }
  return false;
};

export const msToString = (ms) => {
  if (ms === 86400000) {
    return '24:00:00';
  }
  if (ms > 24 * 60 * 60 * 1000) {
    return `${Math.floor(moment.duration(ms).asHours())}:${moment.utc(ms % (60 * 60 * 1000)).format('mm:ss')}`;
  }
  return `${moment.utc(ms).format('HH:mm:ss')}`;
};

// HELPERS:

export const getCanEditUnapproved = (userId, position) => (
  Permissions.has(`TIMECARD_EDIT_${Permissions.formatPosition(position)}`)
    || (userId === Permissions.id && Permissions.has('TIMECARD_EDIT_SELF'))
);

export const getCanEditApproved = (isIndividual, userId, position) => (
  (isIndividual && Permissions.has('APPROVED_TIMECARD_EDIT_SELF'))
    || (userId === Permissions.id && Permissions.has('APPROVED_TIMECARD_EDIT_SELF'))
    || (position && userId !== Permissions.id && Permissions.has(`APPROVED_TIMECARD_EDIT_${Permissions.formatPosition(position)}`))
);

const getWasSwitchedToAnotherProject = (task, nextTask) => (
  task
  && nextTask
  && task.endTime
  && nextTask.startTime
  && (nextTask.startTime - task.endTime) < 250
  && (nextTask.startTime - task.endTime) >= 0
  && task.projectId !== nextTask.projectId
  && task.userId === nextTask.userId
);

export const getGeofenceViolation = ({
  task,
  nextTask,
  projectIdMap,
}) => {
  const {
    startLatitude,
    startLongitude,
    endLatitude,
    endLongitude,
    projectId,
  } = task ?? {};

  const wasSwitchedToAnotherProject = getWasSwitchedToAnotherProject(task, nextTask);

  let geofenceViolation = false;

  const project = projectIdMap?.[projectId] ?? {};

  if (startLatitude && startLongitude) {
    geofenceViolation = TaskHelpers.isGeofenceViolation({
      location: {
        latitude: startLatitude,
        longitude: startLongitude,
      },
      project,
    });
  }

  if (endLatitude && endLongitude && !wasSwitchedToAnotherProject && !geofenceViolation) {
    /*
      Switchs check geofence for the next task's project
      so we will run the check for that task, not ths one
    */

    const endViolation = TaskHelpers.isGeofenceViolation({
      location: {
        latitude: endLatitude,
        longitude: endLongitude,
      },
      project,
    });
    geofenceViolation = geofenceViolation || endViolation;
  }
  return geofenceViolation;
};

export const attachGeofenceViolation = (projectIdMap) => (task, index, tasks) => {
  const geofenceViolation = getGeofenceViolation({
    task,
    nextTask: tasks[index + 1],
    projectIdMap,
  });
  return {
    ...task,
    geofenceViolation,
  };
};

export const decorateTasks = ({
  tasks,
  checkGeofence,
  projectIdMap = {},
  isIndividual,
  canEdit: canEditAll,
  user = {},
  workingHours = {},
  userMap = {},
  tabletMap,
  isSummary,
  settings = {},
}) => (
  tasks.map((task, index) => {
    const {
      projectId,
      state,
      userId,
      enteredMetadata,
      startMetadata,
      endMetadata,
      isPending,
      formUserId,
    } = task;

    // Check if this task was switched to another project by looking at the next task
    const nextTask = tasks[index + 1];

    let {
      id,
      position,
    } = user;
    let canEditSpecificTask = false;
    if (isSummary) {
      const ourUser = userMap?.[userId] ?? {};
      id = ourUser?.id ?? null;
      position = ourUser?.position ?? null;

      canEditSpecificTask = getCanEditUnapproved(id, position);
    }
    const isApproved = state === 'approved';
    const canEditApproved = isApproved && getCanEditApproved(isIndividual, id, position);
    const canEditUnapproved = !isApproved && (canEditAll || canEditSpecificTask);
    const canEditTask = (canEditApproved || canEditUnapproved) && !isPending;
    const {
      [projectId]: project = {},
    } = projectIdMap;

    let geofenceViolation = isSummary ? task.geofenceViolation : false;
    if (checkGeofence) {
      geofenceViolation = getGeofenceViolation({
        task,
        nextTask,
        projectIdMap,
      });
    }

    const {
      enteredViaMetadata,
      enteredViaText,
    } = TaskHelpers.parseEnteredViaMetadata({
      enteredMetadata,
      startMetadata,
      endMetadata,
      userMap,
      tabletMap,
    }) ?? {};

    const isAutoClockOut = task.isAutoClockOut === 1 || task.isAutoClockOut === true;

    return {
      ...task,
      projectName: formatProjectLabelFromCompanySettings({
        name: project.name,
        number: project.number,
        settings,
      }),
      projectNumber: project?.number,
      geofenceViolation,
      isAutoClockOut,
      canEdit: canEditTask,
      outsideWorkingHours: taskIsOutsideWorkingHours(task, workingHours[position] ?? {}),
      enteredViaMetadata,
      enteredVia: enteredViaText,
      formUserName: userMap[formUserId]?.name,
    };
  })
);

/**
 * Gets formatted date for pdf export
 * @param {number} timestamp
 * @returns {string}
 */
const formatDate = (timestamp, zone, task) => {
  const ourDate = TaskHelpers.getTaskDate(task);
  if (ourDate) {
    return ourDate.toLocaleString({
      weekday: 'short',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
  }
  if (!timestamp) return { text: '' };
  return DateTime.fromMillis(timestamp, { zone })
    .toLocaleString({
      weekday: 'short',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
};

/**
 * Gets 12-hour time formatted for pdf export
 * @param {number} timestamp
 * @returns {object}
 */
const formatTime = (timestamp, zone) => {
  if (!timestamp || typeof timestamp !== 'number') return { text: '' };
  const time = DateTime.fromMillis(timestamp, { zone })
    .toLocaleString({
      hour: '2-digit',
      minute: 'numeric',
      timeZoneName: 'short',
    });
  return { text: time || '' };
};

/**
 * Gets formatted duration for pdf export
 * @param {number} runtime
 * @param {object}
 */
const formatRunTime = (runtime) => {
  if (isNullOrUndefined(runtime) || typeof runtime !== 'number') return { text: '' };
  const minutesRounded = Math.round(runtime / 60000);
  const duration = Duration.fromObject({ minutes: minutesRounded }).toFormat('hh:mm');
  return { text: duration ?? '' };
};

export const formatTaskForExport = ({
  t,
  showOriginalTimes,
  task = {},
  phaseMap,
  classMap,
  unionLocalMap,
  unionMap,
  userMap,
  headerSet,
  customColumns,
  divisionIds,
  customFieldMap,
  isFirst,
  exportType,
  timeFormatter,
  dateFormatter,
}) => {
  const {
    projectName,
    costCode,
    phaseId,
    startTime,
    originalStart,
    endTime,
    originalEnd,
    breakStartTime,
    breakEndTime,
    otStartTime,
    otEndTime,
    doubleOTStartTime,
    doubleOTEndTime,
    note,
    type,
    hourBased,
    timezone,
    userId,
    classId,
    enteredVia,
    approvers,
    geofenceViolation,
    isAutoClockOut,
    outsideWorkingHours,
    state,
    duration,
    regularDuration,
    breakDuration,
    otDuration,
    dtOtDuration,
    ptoDuration,
  } = task;

  const phase = phaseMap?.[phaseId]?.name;

  const {
    [classId]: unionClass,
  } = classMap ?? {};

  const {
    [unionClass?.localId]: unionLocal,
  } = unionLocalMap ?? {};

  const {
    [unionLocal?.unionId]: union,
  } = unionMap ?? {};

  let violationValue = '';
  if (geofenceViolation) {
    violationValue += 'This Time Card has a geofence breach. ';
  }
  if (isAutoClockOut) {
    violationValue += 'This Time Card has an automatic clock-out. ';
  }
  if (outsideWorkingHours) {
    violationValue += 'This Time Card is outside of working hours.';
  }

  const taskEndTime = TaskHelpers.getEndTime(task);
  const taskStartTime = TaskHelpers.getStartTime(task);
  const timestamp = taskEndTime || taskStartTime;

  const ourUsername = userMap[userId]?.name;

  const formattedDate = dateFormatter(timestamp, timezone, task);

  const isBreakType = type?.toLowerCase() === 'break';

  const displayStart = TaskHelpers.getDisplayStart({ startTime, originalStart }, showOriginalTimes);
  const displayEnd = TaskHelpers.getDisplayEnd({ endTime, originalEnd }, showOriginalTimes);

  const breakStartToUse = isBreakType ? displayStart : breakStartTime;
  const breakEndToUse = isBreakType ? displayEnd : breakEndTime;

  const values = [
    { key: 'DATE', value: (isFirst || exportType === 'excel') ? formattedDate : '' },
    { key: 'NAME', value: ourUsername },
    { key: 'REGULAR START TIME', value: hourBased || isBreakType ? '' : timeFormatter(displayStart, timezone) },
    { key: 'REGULAR END TIME', value: hourBased || isBreakType ? '' : timeFormatter(displayEnd, timezone) },
    { key: 'BREAK START TIME', value: hourBased ? '' : timeFormatter(breakStartToUse, timezone) },
    { key: 'BREAK END TIME', value: hourBased ? '' : timeFormatter(breakEndToUse, timezone) },
    { key: 'OT START TIME', value: hourBased ? '' : timeFormatter(otStartTime, timezone) },
    { key: 'OT END TIME', value: hourBased ? '' : timeFormatter(otEndTime, timezone) },
    { key: 'DT OT START TIME', value: hourBased ? '' : timeFormatter(doubleOTStartTime, timezone) },
    { key: 'DT OT END TIME', value: hourBased ? '' : timeFormatter(doubleOTEndTime, timezone) },
    { key: 'DURATION', value: duration !== EMPTY_TEXT ? duration : '' },
    { key: 'REGULAR DURATION', value: regularDuration !== EMPTY_TEXT ? regularDuration : '' },
    { key: 'BREAK DURATION', value: breakDuration !== EMPTY_TEXT ? breakDuration : '' },
    { key: 'OT DURATION', value: otDuration !== EMPTY_TEXT ? otDuration : '' },
    { key: 'DT OT DURATION', value: dtOtDuration !== EMPTY_TEXT ? dtOtDuration : '' },
    { key: 'PTO DURATION', value: ptoDuration !== EMPTY_TEXT ? ptoDuration : '' },
    { key: 'TYPE', value: toTitleCase(type) },
    { key: `${t('Project').toUpperCase()} NAME`, value: projectName ?? '' },
    { key: 'PHASE', value: phase ?? '' },
    { key: 'COST CODE', value: costCode ?? '' },
    { key: 'UNION', value: union?.name ?? '' },
    { key: 'LOCAL CLASS', value: unionLocal?.name ?? '' },
    { key: 'WORK CLASS', value: unionClass?.name ?? '' },
    { key: 'NOTES', value: note ?? '' },
    { key: 'ENTERED VIA', value: enteredVia ?? '' },
    {
      key: 'APPROVED BY',
      value: approvers
        ?.filter((approver) => !!approver)
        .map((approver) => approver.name)
        .join(',') ?? '',
    },
    { key: 'VIOLATION', value: violationValue },
  ];

  const row = values.filter((col) => headerSet.has(col.key))
    .map((col) => col.value);

  customColumns?.forEach((fieldId) => {
    const customField = customFieldMap[fieldId];
    const data = task[fieldId];
    if (customField && divisionIds.has(customField.divisionId)) {
      if (customField.columns?.length) {
        customField.columns?.forEach((col) => {
          row.push(data?.[col.key] ?? '');
        });
      } else {
        row.push(data ?? '');
      }
    }
  });

  if (headerSet.has('STATUS')) {
    row.push(state ? toTitleCase(state) : 'Unsubmitted');
  }

  return row;
};

/**
 * Creates a pdf row(s) representing a task
 * @param {boolean} isFirst - is first task of day
 * @param {boolean} isLast - is last task of day
 * @param {number} spacerCount - the number of empty rows between days
 * @param {object} task
 * @param {object} runtimeMap
 * @param {number} roundingInterval
 * @param {object} userMap
 * @param {bool} showOriginalTimes
 * @returns {array} pdf task row(s) to add
 */
const createTaskRow = ({
  t,
  isFirst,
  isLast,
  spacerCount = 3,
  task = {},
  runtimeMap = {},
  roundingInterval,
  userMap = {},
  roundingType,
  roundingSetting,
  showOriginalTimes,
  signatures,
  showSignature,
  phaseMap,
  headerSet,
  unionMap,
  unionLocalMap,
  classMap,
  customColumns,
  customFieldMap,
  divisionIds,
  hasDate,
}) => {
  let rows = [];

  const row = formatTaskForExport({
    t,
    showOriginalTimes,
    task,
    phaseMap,
    classMap,
    unionLocalMap,
    unionMap,
    userMap,
    headerSet,
    customColumns,
    divisionIds,
    customFieldMap,
    isFirst,
    exportType: 'pdf',
    timeFormatter: formatTime,
    dateFormatter: formatDate,
  });

  if (hasDate) {
    if (isFirst) {
      const taskEndTime = TaskHelpers.getEndTime(task);
      const taskStartTime = TaskHelpers.getStartTime(task);
      const timestamp = taskEndTime || taskStartTime;
      row.push(
        formatRunTime(
          roundTotalRuntime(
            runtimeMap[`${task.userId}-${formatDate(timestamp, task.timezone, task)}`],
            roundingInterval,
            roundingType,
            roundingSetting,
          ),
        ),
      );
    } else {
      row.push('');
    }
  }

  if (showSignature) {
    row.push({
      layout: 'noBorders',
      table: {
        headerRow: 0,
        widths: [50, '*'],
        body: signatures,
      },
    });
  }

  rows.push(row);

  if (isLast) {
    const emptySpacerRow = new Array(headerSet.size).fill('');
    rows = rows.concat(Array(spacerCount).fill(emptySpacerRow));
  }
  return rows;
};

/**
 * Creates daily run time map
 * @param {array} tasks
 * @returns {object}
 */
const getDailyRuntimeMap = (tasks = []) => {
  const map = {};
  tasks.forEach((task) => {
    const startTime = TaskHelpers.getStartTime(task);
    const endTime = TaskHelpers.getEndTime(task);
    if (!endTime) return;

    const {
      timezone,
      userId,
    } = task || {};

    const timestamp = endTime || startTime;
    const runtimes = TaskHelpers.getRuntimes(task);
    const runtime = runtimes.regularTime + runtimes.overtime + runtimes.doubleOT;
    const key = `${userId}-${formatDate(timestamp, timezone, task)}`;
    if (!map[key]) {
      map[key] = runtime;
      return;
    }
    map[key] += runtime;
  });
  return map;
};

/**
 * Prepares the task in pdf export format (array of arrays),
 * If there are no tasks return empty row
 * @param {object} t - i18n translation function
 * @param {number} roundingInterval
 * @param {bool} isSummary
 * @param {object} userMap
 * @param {array} tasks
 * @param {string} roundingType
 * @param {bool} showOriginalTimes - Whether to show original or mutating times
 * @returns {string[][]} - pdf rows
 */
const refinePDFExportTaskData = async ({
  t,
  roundingInterval,
  isSummary,
  userMap,
  tasks = [],
  roundingType,
  roundingSetting,
  showOriginalTimes,
  dataMap,
  fileMap,
  summaryExportFields,
  showSignature,
  phaseMap,
  headerSet,
  unionMap,
  unionLocalMap,
  classMap,
  customColumns,
  customFieldMap,
  divisionIds,
  hasDate,
}) => {
  let sortedTasks = tasks;
  if (!isSummary) {
    sortedTasks = tasks
      .map((task) => ({ ...task })) // use map to return new tasks to allow sort (mutability)
      .sort((a, b) => TaskHelpers.getEndTime(a) - TaskHelpers.getEndTime(b));
  }
  const dailyRuntimeMap = getDailyRuntimeMap(sortedTasks);
  const dayLabelsAdded = new Set();
  const taskRows = await Promise.all(sortedTasks.map(async (task, index) => {
    const {
      id,
      timezone,
      userId,
    } = task || {};
    const nextTask = sortedTasks[index + 1] || {};
    const currentTaskStartTimeStamp = TaskHelpers.getStartTime(task);
    const currentTaskEndTimeStamp = TaskHelpers.getEndTime(task);
    const nextTaskStartTimeStamp = TaskHelpers.getStartTime(nextTask);
    const nextTaskEndTimeStamp = TaskHelpers.getEndTime(nextTask);
    const currentTaskTS = currentTaskEndTimeStamp || currentTaskStartTimeStamp;
    const nextTaskTS = nextTaskStartTimeStamp || nextTaskEndTimeStamp;
    const currentTaskFormattedDateKey = `${userId}-${formatDate(currentTaskTS, timezone, task)}`;
    const nextTaskFormattedDateKey = `${userId}-${formatDate(nextTaskTS, timezone, task)}`;
    let isFirstTaskOfDay = false;
    let isLastTaskOfDay = false;
    if (!dayLabelsAdded.has(currentTaskFormattedDateKey)) {
      isFirstTaskOfDay = true;
      dayLabelsAdded.add(currentTaskFormattedDateKey);
    }
    if (nextTaskFormattedDateKey !== currentTaskFormattedDateKey) {
      isLastTaskOfDay = true;
    }

    const { data: customData = [], divisionId } = dataMap[id] ?? {};

    const signatures = [];
    if (showSignature && divisionId) {
      const signatureField = summaryExportFields[divisionId];
      const customFieldResponseMap = generateResponseMap(customData);
      const { values = [] } = customFieldResponseMap[signatureField] ?? {};
      await Promise.all(values.map(async ({ sig, name }) => {
        const { url } = fileMap[sig] ?? {};
        if (url) {
          let image;
          try {
            image = await getBase64ImageFromURL(url);
          } catch (error) {
            // pass
          }
          let signature = { text: '' };
          if (image) {
            signature = { image, fit: [100, 50] };
          }
          signatures.push([{ text: name }, signature]);
        }
      }));
    }

    const row = createTaskRow({
      t,
      isFirst: isFirstTaskOfDay,
      isLast: isLastTaskOfDay,
      task,
      runtimeMap: dailyRuntimeMap,
      roundingInterval,
      userMap,
      roundingType,
      roundingSetting,
      showOriginalTimes,
      signatures: signatures.length ? signatures : [{ text: '' }],
      showSignature,
      phaseMap,
      headerSet,
      unionMap,
      unionLocalMap,
      classMap,
      customFieldMap,
      customColumns,
      divisionIds,
      hasDate,
    });

    return row;
  }));
  return taskRows.flat();
};

/*
  HARBOUR-6373 https://github.com/bpampuch/pdfmake/issues/441
  Open issue with pdfmake where columns are cut off if the table is too wide.
  We will use bigger letter sizes in this scenario using a 'min col width'
*/
const MIN_COL_WIDTH = 75;
const A3_PORTRAIT_WIDTH = 842;
const A_SIZES_LANDSCAPE = [
  { name: 'A0', width: 3370 },
  { name: 'A1', width: 2384 },
  { name: 'A2', width: 1684 },
  { name: 'A3', width: 1191 },
];

const getPdfProperties = (columns) => {
  const minColWidth = columns.reduce((acc, col) => {
    const width = col.key === 'SIGNATURE' ? 150 : MIN_COL_WIDTH;
    return acc + width;
  }, 0);

  const widths = columns.map((col) => (col.key === 'SIGNATURE' ? 150 : '*'));

  // portrait
  if (minColWidth < A3_PORTRAIT_WIDTH) {
    return {
      pageOrientation: 'portrait',
      widths,
      pageSize: 'A3',
    };
  }

  let pageSize = 'A3';

  A_SIZES_LANDSCAPE.forEach((size) => {
    if (minColWidth < size.width) {
      pageSize = size.name;
    }
  });

  return {
    pageOrientation: 'landscape',
    widths,
    pageSize,
  };
};

const getPDFSummaryHeader = (hasPTO) => {
  const headers = [
    { text: 'REGULAR:', style: 'tableHeader' },
    { text: 'OVERTIME:', style: 'tableHeader' },
    { text: 'DOUBLE OVERTIME:', style: 'tableHeader' },
  ];
  if (hasPTO) {
    headers.push({ text: 'PTO:', style: 'tableHeader' });
  }
  return headers.concat([{ text: 'TOTAL PAY:', style: 'tableHeader' }]);
};

/**
 * Adjusts/prepares timecard data structure for pdf export
 * @param {object} t - i18n translation function
 * @param {object} companyImageURL
 * @param {object} user
 * @param {number} start - millis
 * @param {number} end - millis
 * @param {array} tasks
 */
export const timecardPDFExportAdapter = async ({
  t,
  companyImageURL,
  user: {
    name: userName = '',
    employeeId = '',
  } = {},
  start = 0,
  end = 0,
  tasks,
  roundingInterval,
  isSummary,
  userMap = {},
  roundingType,
  roundingSetting,
  showOriginalTimes,
  summaryExportFields = {},
  phaseMap = {},
  mainColumns,
  customColumns,
  customFieldMap = {},
  divisionIds = new Set(),
  unionMap = {},
  unionLocalMap = {},
  classMap = {},
  regularHours,
  otHours,
  doubleOtHours,
  ptoHours,
  totalPay,
}) => {
  const pdfTableExport = new PDFTableExport();
  // Prepare/Construct Header:
  const logoEntry = await PDFTableExport.getCompanyLogo(companyImageURL);
  const timeRangeLabel = PDFTableExport.getTimeRangeLabel(start, end);
  const leftColumn = PDFTableExport.createColumnList({
    content: [
      { text: isSummary ? 'Summary' : userName, style: 'header' },
      employeeId ? { text: `ID: ${employeeId}`, style: 'subHeader' } : { text: '' },
    ],
  });
  const rightColumn = PDFTableExport.createColumnList({
    content: [
      { text: timeRangeLabel, style: 'header' },
      { text: '' },
    ],
  });

  const hasPTO = (ptoHours ?? 0) > 0;
  const pdfHeaderColumns = getPDFSummaryHeader(hasPTO);
  const ptoBodyRow = [regularHours, otHours, doubleOtHours];
  if (hasPTO) {
    ptoBodyRow.push(ptoHours);
  }
  ptoBodyRow.push(totalPay);
  const summaryTable = isSummary
    ? null
    : PDFTableExport.createTable({
      widths: Array(pdfHeaderColumns.length).fill('auto'),
      header: pdfHeaderColumns,
      rows: [ptoBodyRow],
    });
  const header = PDFTableExport.createHeader({
    useLogo: true,
    logoEntry,
    columns: [leftColumn, rightColumn, summaryTable],
  });

  const { dataMap, fileMap } = await loadManualEntryData(tasks.map(({ id }) => id), true);
  const showSignature = shouldShowSignature({ tasks, summaryExportFields, dataMap });
  // Prepare/Construct Body:

  const colMap = getViewColumnToExportMap(t);

  // HARBOUR-8153: If columns have been disabled, mainColumns is an empty array
  const includeAllColumns = !mainColumns;

  const mainColSet = new Set(
    (mainColumns ?? []).map((col) => colMap[col]),
  );

  const tableHeader = getBasePDFHeader(t).filter((col) => (
    includeAllColumns || mainColSet.has(col.key)
  ));

  customColumns?.forEach((fieldId) => {
    const customField = customFieldMap[fieldId];

    if (customField && divisionIds.has(customField.divisionId)) {
      if (customField?.columns?.length) {
        customField.columns?.forEach((col) => {
          tableHeader.push({ key: col.key, text: toTitleCase(`${customField.title} - ${col.name}`), style: 'tableHeader' });
        });
      } else {
        tableHeader.push({ key: fieldId, text: toTitleCase(customField.title), style: 'tableHeader' });
      }
    }
  });

  const hasDate = tableHeader.some((col) => col.key === 'DATE');
  const hasStatus = mainColSet.has('STATUS');

  if (hasStatus) {
    tableHeader.push({ key: 'STATUS', text: 'Status', style: 'tableHeader' });
  }

  if (hasDate) {
    tableHeader.push({ key: 'TOTAL', text: 'Total', style: 'tableHeader' });
  }

  if (showSignature) {
    tableHeader.push({ key: 'SIGNATURE', text: 'Signature', style: 'tableHeader' });
  }

  const headerSet = new Set(tableHeader.map((col) => col.key));

  const bodyRows = await refinePDFExportTaskData({
    t,
    roundingInterval: roundingInterval || 1,
    isSummary,
    userMap,
    tasks,
    roundingType,
    roundingSetting,
    showOriginalTimes,
    dataMap,
    fileMap,
    summaryExportFields,
    showSignature,
    phaseMap,
    headerSet,
    unionMap,
    unionLocalMap,
    classMap,
    customColumns,
    customFieldMap,
    divisionIds,
    hasDate,
  });

  const {
    widths,
    pageOrientation,
    pageSize,
  } = getPdfProperties(tableHeader);

  const bodyTable = PDFTableExport.createTable({
    widths,
    header: tableHeader,
    rows: bodyRows,
  });

  // Export PDF:
  const intervalStart = DateTime.fromMillis(start).toLocaleString(DateTime.DATE_MED);
  const intervalEnd = DateTime.fromMillis(end).toLocaleString(DateTime.DATE_MED);
  const title = `${isSummary ? 'Summary' : userName} ${intervalStart} - ${intervalEnd} Timecard Breakdown`;
  pdfTableExport.export({
    name: title,
    header,
    body: [bodyTable],
    pageOrientation,
    pageSize,
  });
};

export const getRuntime = ({
  startTime,
  endTime,
  type,
}) => {
  if (!(endTime && endTime > startTime) || type === 'break') {
    return 0;
  }

  return endTime - startTime;
};

export const roundDayRuntime = (runtime, roundingInterval, roundingType, roundingSetting) => {
  if (roundingType === 'taskTime') return runtime;

  return TaskHelpers.getRoundedRuntime(
    runtime,
    { roundingInterval, roundingSetting },
  );
};

/**
 * Converts redistribution rows from decimal-hour format (as edited in the UI) into
 * the API payload format: each time-type value becomes a millisecond timestamp on
 * the given date (hours/minutes from the decimal, date from `date`).
 */
export const prepareRedistributionPayload = ({ distributionRows = [], date }) => {
  const newPayload = distributionRows.map((row) => {
    const newRow = { ...row };

    Object.keys(timeKeyToTitle).forEach((key) => {
      if (!newRow[key]) return;
      if (newRow[key] === '0') {
        newRow[key] = null;
        return;
      }
      const parsedValue = parseFloat(newRow[key]);
      const decimalValue = Number.isNaN(parsedValue) ? 0 : parsedValue;
      const hours = Math.floor(decimalValue);
      const minutes = Math.round((decimalValue - hours) * 60);
      const millis = date.set({ hours, minutes }).toMillis();
      newRow[key] = millis;
    });

    return newRow;
  });

  return newPayload;
};

/**
 * Builds per-task hour breakdowns from stored segments (regular/OT/DT) plus deriveBuckets
 * for Sat/Sun labeling. Manual OT + overallTime applies day rounding on read (not stored).
 * Range is the visible timecard window (no OT buffer).
 */
export const buildHoursBreakdownMap = ({
  userIds = [],
  timeEntryUserMap = {},
  companySettings = {},
  settingsMap = {},
  range,
  isApprovals = false,
  approvalSet = new Set(),
}) => {
  const [startDT, endDT] = range ?? [];
  const calculatedOtTasksMap = {};

  userIds.forEach((uid) => {
    const usersTasks = timeEntryUserMap?.[uid] ?? [];
    const settings = settingsMap[uid] ?? companySettings;
    const filteredTasks = usersTasks.filter((task) => {
      if (!isGoodTask(task)) return false;
      if (!startDT || !endDT) return true;
      const date = TaskHelpers.getTaskDate(task);
      if (!date) return false;
      return !(date < startDT || date > endDT) || (isApprovals && approvalSet.has(task.id));
    });
    calculatedOtTasksMap[uid] = deriveTasksWithBuckets({
      tasks: filteredTasks,
      settings,
    })
      .reduce((acc, task) => {
        const {
          id,
          regularTime = 0,
          breakTime = 0,
          overtime = 0,
          doubleOT = 0,
          saturdayTime = 0,
          saturdayOT = 0,
          saturdayDoubleOT = 0,
          sundayTime = 0,
          sundayOT = 0,
          sundayDoubleOT = 0,
        } = task;
        acc[id] = {
          ...task,
          regularDuration: regularTime + saturdayTime + sundayTime,
          breakDuration: breakTime,
          otDuration: overtime + saturdayOT + sundayOT,
          dtDuration: doubleOT + saturdayDoubleOT + sundayDoubleOT,
        };
        return acc;
      }, {});
  });

  return calculatedOtTasksMap;
};

// Visible timecard/report window — no OT buffer (stored segments include weekly OT).
export const getTimesFromDisplayTimeRange = (timeRange = []) => {
  const [startDT, endDT] = timeRange;
  return {
    startTime: startDT.startOf('day').toMillis(),
    endTime: endDT.endOf('day').toMillis(),
  };
};

export const getUsersFromSelectedDivisions = ({
  users,
  selectedDivisions,
  divisions,
  shouldShowArchived,
}) => {
  const selectedDivUsers = Object.values(divisions)
    .filter(({ id }) => selectedDivisions.has(id))
    .map(({ users: divUsers = new Set() }) => divUsers);
  const mergedSelectedDivUsers = mergeSets(selectedDivUsers);
  return users.filter((user) => (
    mergedSelectedDivUsers.has(user.id)
    && (user.active || shouldShowArchived)
  ));
};

export const hasEditedTimes = (editedTask, originalTask) => {
  if (!editedTask || !originalTask || editedTask.type === 'break') return false;

  const {
    startTime: eStart,
    endTime: eEnd,
    otStartTime: eOtStart,
    otEndTime: eOtEnd,
    breakStartTime: eBreakStart,
    breakEndTime: eBreakEnd,
    doubleOTStartTime: eDOtStart,
    doubleOTEndTime: eDOtEnd,
    ptoStartTime: ePtoStart,
    ptoEndTime: ePtoEnd,
    originalStart: eOriginalStart,
    originalEnd: eOriginalEnd,
  } = editedTask;
  const {
    startTime,
    endTime,
    otStartTime,
    otEndTime,
    breakStartTime,
    breakEndTime,
    doubleOTStartTime,
    doubleOTEndTime,
    ptoStartTime,
    ptoEndTime,
    originalStart,
    originalEnd,
  } = originalTask;

  /* eslint-disable max-len */
  return (
    (eStart && startTime && stripSecondsAndMilliseconds(eStart) !== stripSecondsAndMilliseconds(startTime))
    || (eEnd && endTime && stripSecondsAndMilliseconds(eEnd) !== stripSecondsAndMilliseconds(endTime))
    || (eOtStart && otStartTime && stripSecondsAndMilliseconds(eOtStart) !== stripSecondsAndMilliseconds(otStartTime))
    || (eOtEnd && otEndTime && stripSecondsAndMilliseconds(eOtEnd) !== stripSecondsAndMilliseconds(otEndTime))
    || (eBreakStart && breakStartTime && stripSecondsAndMilliseconds(eBreakStart) !== stripSecondsAndMilliseconds(breakStartTime))
    || (eBreakEnd && breakEndTime && stripSecondsAndMilliseconds(eBreakEnd) !== stripSecondsAndMilliseconds(breakEndTime))
    || (eDOtStart && doubleOTStartTime && stripSecondsAndMilliseconds(eDOtStart) !== stripSecondsAndMilliseconds(doubleOTStartTime))
    || (eDOtEnd && doubleOTEndTime && stripSecondsAndMilliseconds(eDOtEnd) !== stripSecondsAndMilliseconds(doubleOTEndTime))
    || (ePtoStart && ptoStartTime && stripSecondsAndMilliseconds(ePtoStart) !== stripSecondsAndMilliseconds(ptoStartTime))
    || (ePtoEnd && ptoEndTime && stripSecondsAndMilliseconds(ePtoEnd) !== stripSecondsAndMilliseconds(ptoEndTime))
    || (eOriginalStart && originalStart && stripSecondsAndMilliseconds(originalStart) !== stripSecondsAndMilliseconds(eOriginalStart))
    || (eOriginalEnd && originalEnd && stripSecondsAndMilliseconds(eOriginalEnd) !== stripSecondsAndMilliseconds(originalEnd))
  );
  /* eslint-enable max-len */
};

export const getPendingTaskText = ({
  formUserName,
  formCreatedAt,
  formName,
  formNumber,
}) => {
  const submitterName = formUserName ?? 'Unknown';
  const formattedCreated = formCreatedAt
    ? DateTime.fromMillis(formCreatedAt).toLocaleString(DateTime.DATETIME_MED)
    : null;

  let formText = '';
  if (formNumber) formText += `${formNumber} - `;
  if (formName) formText += formName;

  return `Pending entry from form ${formText}, submitted by ${submitterName} on ${formattedCreated}`;
};
